import React, { useEffect, useState } from 'react';
import Navbar from '@/components/zera/Navbar';
import Footer from '@/components/zera/Footer';
import ZeraPartners from '@/components/zera/ZeraPartners';
import StoriesPlanner from '@/components/zera/StoriesPlanner';
import { fetchPlan, PlanDay } from '@/lib/contentPlanner';
import {
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  MoreHorizontal,
  Calendar,
  Clock,
  Hash,
  Copy,
  Check,
  Instagram,
  Grid3x3,
  Sparkles,
} from 'lucide-react';

interface InstagramPost {
  day: string;
  date: string;
  postTime: string;
  type: 'Feed Post' | 'Reel' | 'Carousel' | 'Story';
  theme: string;
  image: string;
  caption: string;
  hashtags: string[];
  cta: string;
  approval?: string;
}

// ---- Static fallback (used if DB is empty / not yet seeded) ----
const fallbackPosts: InstagramPost[] = [
  {
    day: 'Monday',
    date: 'Week 1 · Day 1',
    postTime: '9:00 AM',
    type: 'Feed Post',
    theme: 'Aspirational Opener',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900188372_819697da.jpg',
    caption:
      "Some mornings the Ionian feels less like a sea and more like a secret.\n\nThis is where every Zera journey begins — turquoise water, sun-warmed teak, and a coastline most of the world hasn't discovered yet.\n\nWhere should we drop anchor next?",
    hashtags: ['#ZeraTravel', '#AlbanianRiviera', '#LuxuryYachtCharter', '#HiddenMediterranean', '#TravelInStyle'],
    cta: 'Tap the link in bio to plan your private charter.',
  },
  {
    day: 'Tuesday',
    date: 'Week 1 · Day 2',
    postTime: '12:30 PM',
    type: 'Carousel',
    theme: 'Villa Showcase',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900206532_bc1c9e7b.jpg',
    caption:
      "A terrace. An olive grove. An entire coastline to yourself.\n\nOur curated villas aren't just stays — they're the setting for the kind of evenings you remember for years. Long dinners. Quiet swims. The slow honey light of an Albanian sunset.\n\nSwipe to step inside →",
    hashtags: ['#PrivateVilla', '#AlbanianVilla', '#LuxuryStays', '#ZeraVillas', '#MediterraneanLife', '#SlowTravel'],
    cta: 'Reserve yours — link in bio.',
  },
  {
    day: 'Wednesday',
    date: 'Week 1 · Day 3',
    postTime: '6:00 PM',
    type: 'Reel',
    theme: 'Cultural Heritage',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900230017_1f4f6719.png',
    caption:
      'Berat is called the City of a Thousand Windows — and at blue hour, every one of them tells a story.\n\nOttoman stone. Byzantine churches. UNESCO-protected streets where time moves at the pace of a slow espresso.\n\nThis is the Albania our travelers fall in love with. The one beyond the postcards.',
    hashtags: ['#BeratAlbania', '#UNESCO', '#CulturalTravel', '#HiddenEurope', '#ZeraJourneys', '#HistoricEurope'],
    cta: 'Read our Berat guide — link in bio.',
  },
  {
    day: 'Thursday',
    date: 'Week 1 · Day 4',
    postTime: '1:00 PM',
    type: 'Feed Post',
    theme: 'Culinary Story',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900255094_23de8646.png',
    caption:
      "Lunch, the Albanian way.\n\nFish pulled from the Ionian at dawn. Olive oil pressed three villages over. A glass of Kallmet that tastes like the hills it came from.\n\nWe build journeys around tables like this — because the real souvenir is the meal you'll keep talking about.",
    hashtags: ['#AlbanianCuisine', '#FoodieTravel', '#MediterraneanDiet', '#CulinaryJourney', '#ZeraTable', '#FarmToCoast'],
    cta: 'Ask us about our private chef experiences.',
  },
  {
    day: 'Friday',
    date: 'Week 1 · Day 5',
    postTime: '8:00 AM',
    type: 'Reel',
    theme: 'Adventure & Nature',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900274375_d7ea979e.png',
    caption:
      'Up here, above the cloud line in Theth, you understand why locals call the Albanian Alps the Accursed Mountains — too beautiful to belong to anyone.\n\nFor the traveler who wants more than a beach chair, this is the other Albania. Wild. Quiet. Yours for a morning.\n\nReady to hike it with us?',
    hashtags: ['#TheAlbanianAlps', '#TheGuide', '#AdventureTravel', '#HikingEurope', '#ZeraExpeditions', '#WildEurope'],
    cta: 'Build your custom itinerary — link in bio.',
  },
  {
    day: 'Saturday',
    date: 'Week 1 · Day 6',
    postTime: '11:00 AM',
    type: 'Carousel',
    theme: 'Heritage Deep Dive',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900296496_29e9bfa3.png',
    caption:
      "2,500 years of empires standing in a single afternoon's walk.\n\nButrint isn't just ruins — it's Greek, Roman, Byzantine, Venetian, and Ottoman, layered like pages in a book the Mediterranean forgot to publish.\n\nOur private guides bring it back to life. Save this post for your next planning session.",
    hashtags: ['#ButrintAlbania', '#UNESCOHeritage', '#AncientHistory', '#ArchaeologyTravel', '#ZeraTravel', '#HistoricalJourneys'],
    cta: 'Book a private heritage tour — link in bio.',
  },
  {
    day: 'Sunday',
    date: 'Week 1 · Day 7',
    postTime: '5:30 PM',
    type: 'Feed Post',
    theme: 'Lifestyle & Aspiration',
    image: 'https://d64gsuwffb70l.cloudfront.net/69cbf6b38dc4f4af5d55476d_1779900313956_ee63e4c0.jpg',
    caption:
      "Sunday in Gjirokastër.\n\nNo agenda. No notifications. Just stone streets, a slow walk, and the quiet luxury of having nowhere else to be.\n\nThis is what we mean when we say we don't sell trips — we curate slower lives, one week at a time.\n\nWhere will yours unfold?",
    hashtags: ['#Gjirokaster', '#SlowTravel', '#LuxuryLifestyle', '#ZeraMoments', '#TravelInspiration', '#QuietLuxury'],
    cta: "DM us 'JOURNEY' to start planning.",
  },
];

const typeColors: Record<InstagramPost['type'], string> = {
  'Feed Post': 'bg-[#C9A961]/15 text-[#C9A961] border-[#C9A961]/30',
  Reel: 'bg-[#1A3B5C]/10 text-[#1A3B5C] border-[#1A3B5C]/30',
  Carousel: 'bg-[#8B6F47]/15 text-[#8B6F47] border-[#8B6F47]/30',
  Story: 'bg-rose-100 text-rose-700 border-rose-300',
};

const approvalBadge: Record<string, string> = {
  Draft: 'bg-slate-100 text-slate-600 border-slate-300',
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  Scheduled: 'bg-[#C9A961]/15 text-[#8B6F47] border-[#C9A961]/40',
};

// ---- DB → presentational converters ----

const planToPosts = (days: PlanDay[]): InstagramPost[] => {
  return days
    .map((d) => {
      const f = d.frames[0];
      if (!f) return null;
      const t = (['Feed Post', 'Reel', 'Carousel', 'Story'] as const).includes(f.post_type as any)
        ? (f.post_type as InstagramPost['type'])
        : 'Feed Post';
      return {
        day: d.day_label,
        date: d.date_label,
        postTime: f.post_time,
        type: t,
        theme: d.theme,
        image: f.image,
        caption: f.caption,
        hashtags: f.hashtags,
        cta: f.cta,
        approval: f.approval_status,
      } as InstagramPost;
    })
    .filter((x): x is InstagramPost => !!x);
};

const planToStoryDays = (days: PlanDay[]) => {
  return days.map((d) => ({
    day: d.day_label,
    date: d.date_label,
    theme: d.theme,
    narrative: d.narrative,
    frames: d.frames.map((f, i) => ({
      index: i + 1,
      image: f.image,
      headline: f.headline,
      copy: f.copy,
      stickers: f.stickers as any,
      linkDestination: f.link_destination || undefined,
      textColor: 'light' as const,
    })),
  }));
};

interface InstagramContentProps {
  embedded?: boolean;
}

const InstagramContent: React.FC<InstagramContentProps> = ({ embedded = false }) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'feed' | 'stories'>('feed');
  const [posts, setPosts] = useState<InstagramPost[]>(fallbackPosts);
  const [storyDays, setStoryDays] = useState<ReturnType<typeof planToStoryDays>>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const [feed, stories] = await Promise.all([fetchPlan('feed'), fetchPlan('stories')]);
      if (feed.length > 0) setPosts(planToPosts(feed));
      if (stories.length > 0) setStoryDays(planToStoryDays(stories));
      setLoaded(true);
    })();
  }, []);

  const handleCopy = (post: InstagramPost, index: number) => {
    const text = `${post.caption}\n\n${post.cta}\n\n${post.hashtags.join(' ')}`;
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Total stories count for tab pill
  const totalStoryFrames = storyDays.reduce((sum, d) => sum + d.frames.length, 0) || 30;

  return (
    <div className={embedded ? 'bg-[#FAF8F3]' : 'min-h-screen bg-[#FAF8F3]'}>
      {!embedded && <Navbar />}

      {/* Page hero — hidden in embedded/admin mode */}
      {!embedded && (
        <section className="pt-40 pb-16 bg-gradient-to-b from-[#0D1B2A] to-[#1A3B5C] text-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
            <div className="flex items-center justify-center gap-2 text-[#C9A961] mb-6">
              <Instagram className="w-5 h-5" />
              <span className="font-sans text-[11px] tracking-[0.3em] uppercase">
                @zeratravel · content calendar
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl font-light tracking-wide mb-6">
              A Week in the Life of Zera
            </h1>
            <div className="w-16 h-[2px] bg-[#C9A961] mx-auto mb-6" />
            <p className="font-sans text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Seven days of curated Instagram content — captions, creative direction, hashtags, and posting cadence.
              Built to feel like the brand: slow, considered, and unmistakably Albania.
            </p>
          </div>
        </section>
      )}

      {/* Tab switcher — sticky beneath navbar (or at top in embedded mode) */}
      <section
        className={`${embedded ? 'sticky top-0' : 'sticky top-20'} z-30 bg-[#FAF8F3]/95 backdrop-blur-md border-b border-[#C9A961]/20`}
      >

        <div className="max-w-6xl mx-auto px-6 lg:px-12 flex items-center justify-center gap-2 py-4">
          <button
            onClick={() => setActiveTab('feed')}
            className={`group inline-flex items-center gap-2 px-5 py-3 border font-sans text-[11px] tracking-[0.25em] uppercase transition-all duration-300 ${
              activeTab === 'feed'
                ? 'bg-[#1A3B5C] text-white border-[#1A3B5C]'
                : 'bg-white text-[#1A3B5C] border-[#C9A961]/40 hover:border-[#C9A961] hover:bg-[#F3EFE6]'
            }`}
          >
            <Grid3x3 className="w-4 h-4" />
            Feed Posts
            <span className={`ml-1 text-[10px] ${activeTab === 'feed' ? 'text-[#C9A961]' : 'text-[#8B6F47]'}`}>
              {posts.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('stories')}
            className={`group inline-flex items-center gap-2 px-5 py-3 border font-sans text-[11px] tracking-[0.25em] uppercase transition-all duration-300 ${
              activeTab === 'stories'
                ? 'bg-[#1A3B5C] text-white border-[#1A3B5C]'
                : 'bg-white text-[#1A3B5C] border-[#C9A961]/40 hover:border-[#C9A961] hover:bg-[#F3EFE6]'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Stories This Week
            <span
              className={`ml-1 text-[10px] ${activeTab === 'stories' ? 'text-[#C9A961]' : 'text-[#8B6F47]'}`}
            >
              {totalStoryFrames}
            </span>
          </button>
        </div>
      </section>

      {activeTab === 'feed' && (
        <>
          {/* Strategy summary */}
          <section className="py-12 border-b border-[#C9A961]/20 bg-white">
            <div className="max-w-6xl mx-auto px-6 lg:px-12 grid md:grid-cols-4 gap-8">
              {[
                { label: 'Posts This Week', value: String(posts.length) },
                { label: 'Content Mix', value: 'Feed · Reels · Carousels' },
                { label: 'Posting Window', value: '8 AM – 6 PM' },
                { label: 'Voice', value: 'Quiet Luxury' },
              ].map((stat) => (
                <div key={stat.label} className="text-center md:text-left">
                  <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-2">
                    {stat.label}
                  </div>
                  <div className="font-serif text-xl text-[#1A3B5C]">{stat.value}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Content calendar */}
          <section className="py-20">
            <div className="max-w-6xl mx-auto px-6 lg:px-12">
              <div className="text-center mb-16">
                <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#C9A961]">
                  The Calendar
                </span>
                <h2 className="font-serif text-3xl md:text-5xl text-[#1A3B5C] mt-3 font-light">
                  {posts.length} Post{posts.length === 1 ? '' : 's'}, One Story
                </h2>
                <div className="w-12 h-[2px] bg-[#C9A961] mx-auto mt-6" />
              </div>

              <div className="space-y-16">
                {posts.map((post, index) => (
                  <div key={`${post.day}-${index}`} className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                    {/* Instagram mockup */}
                    <div className="lg:sticky lg:top-28">
                      <div className="bg-white rounded-2xl shadow-xl shadow-[#1A3B5C]/10 overflow-hidden border border-[#C9A961]/20 max-w-[460px] mx-auto">
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#C9A961] to-[#8B6F47] flex items-center justify-center">
                              <span className="font-serif text-white text-sm tracking-wider">Z</span>
                            </div>
                            <div>
                              <div className="font-sans text-[13px] font-semibold text-[#1A3B5C]">zeratravel</div>
                              <div className="font-sans text-[10px] text-gray-500">
                                Albanian Riviera · Sponsored
                              </div>
                            </div>
                          </div>
                          <MoreHorizontal className="w-5 h-5 text-gray-500" />
                        </div>

                        {/* Image */}
                        <div className="relative aspect-square bg-gray-100">
                          {post.image ? (
                            <img src={post.image} alt={post.theme} className="w-full h-full object-cover" />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-slate-300 font-sans text-xs">
                              No image
                            </div>
                          )}
                          {post.type === 'Reel' && (
                            <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-md px-2 py-1 text-white text-[10px] tracking-wider font-sans">
                              REEL
                            </div>
                          )}
                          {post.type === 'Carousel' && (
                            <div className="absolute top-3 right-3 flex gap-1">
                              {[1, 2, 3].map((d) => (
                                <span key={d} className="w-1.5 h-1.5 rounded-full bg-white/80" />
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="px-4 pt-3 pb-2 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <Heart className="w-6 h-6 text-gray-800" />
                            <MessageCircle className="w-6 h-6 text-gray-800" />
                            <Send className="w-6 h-6 text-gray-800" />
                          </div>
                          <Bookmark className="w-6 h-6 text-gray-800" />
                        </div>

                        {/* Likes + caption preview */}
                        <div className="px-4 pb-4">
                          <div className="font-sans text-[13px] font-semibold text-[#1A3B5C] mb-1">
                            {(1200 + index * 247).toLocaleString()} likes
                          </div>
                          <div className="font-sans text-[13px] text-gray-800 leading-relaxed whitespace-pre-line">
                            <span className="font-semibold text-[#1A3B5C]">zeratravel</span>{' '}
                            {(post.caption || '').split('\n')[0]}
                            <button className="text-gray-500 ml-1">... more</button>
                          </div>
                          <div className="font-sans text-[10px] text-gray-400 tracking-wider mt-2 uppercase">
                            {post.postTime} · {post.day}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Content details */}
                    <div className="space-y-6">
                      {/* Day header */}
                      <div className="flex items-center justify-between flex-wrap gap-4 border-b border-[#C9A961]/30 pb-5">
                        <div>
                          <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961] mb-2">
                            {post.date}
                          </div>
                          <h3 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] font-light">{post.day}</h3>
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          <div className="flex items-center gap-2">
                            {post.approval && (
                              <span
                                className={`px-2 py-1 border text-[9px] tracking-[0.2em] uppercase font-sans rounded-full ${
                                  approvalBadge[post.approval] || approvalBadge.Draft
                                }`}
                              >
                                {post.approval}
                              </span>
                            )}
                            <span
                              className={`px-3 py-1 border text-[10px] tracking-[0.2em] uppercase font-sans ${typeColors[post.type]}`}
                            >
                              {post.type}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-[11px] text-[#1A3B5C]/60 font-sans">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {post.postTime}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {post.theme}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Caption */}
                      <div>
                        <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3">
                          Caption
                        </div>
                        <p className="font-sans text-[15px] text-[#1A3B5C]/85 leading-[1.8] whitespace-pre-line">
                          {post.caption}
                        </p>
                        {post.cta && (
                          <p className="font-sans text-[13px] text-[#8B6F47] italic mt-4 border-l-2 border-[#C9A961] pl-4">
                            CTA: {post.cta}
                          </p>
                        )}
                      </div>

                      {/* Hashtags */}
                      {post.hashtags.length > 0 && (
                        <div>
                          <div className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#C9A961] mb-3 flex items-center gap-2">
                            <Hash className="w-3 h-3" /> Hashtags
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {post.hashtags.map((tag) => (
                              <span
                                key={tag}
                                className="font-sans text-[12px] text-[#1A3B5C] bg-[#F3EFE6] border border-[#C9A961]/30 px-3 py-1.5 rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Copy button */}
                      <button
                        onClick={() => handleCopy(post, index)}
                        className="inline-flex items-center gap-2 px-5 py-3 bg-[#1A3B5C] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-[#C9A961] transition-colors duration-300"
                      >
                        {copiedIndex === index ? (
                          <>
                            <Check className="w-4 h-4" /> Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" /> Copy Full Post
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Notes section */}
          <section className="py-20 bg-[#F3EFE6] border-t border-[#C9A961]/20">
            <div className="max-w-4xl mx-auto px-6 lg:px-12">
              <div className="text-center mb-12">
                <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#C9A961]">
                  Brand Notes
                </span>
                <h2 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] mt-3 font-light">
                  Voice &amp; Visual Direction
                </h2>
                <div className="w-12 h-[2px] bg-[#C9A961] mx-auto mt-6" />
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    title: 'Tone',
                    body:
                      'Confident, unhurried, evocative. We never use exclamation marks more than once per post. We write like a well-traveled friend, not a brochure.',
                  },
                  {
                    title: 'Visual Palette',
                    body:
                      'Cream, deep navy, antique gold, sun-warmed stone. Always natural light. Golden hour and blue hour are home.',
                  },
                  {
                    title: 'What We Avoid',
                    body:
                      'Stock photography, generic travel clichés, "bucket list" language, over-edited filters, and anything that feels mass-market.',
                  },
                  {
                    title: 'Engagement Strategy',
                    body:
                      'Always end with a soft question or invitation. Reply to first 20 comments within 2 hours. DM is our concierge — treat it that way.',
                  },
                ].map((note) => (
                  <div key={note.title} className="bg-white border border-[#C9A961]/30 p-8">
                    <h3 className="font-serif text-xl text-[#1A3B5C] mb-3">{note.title}</h3>
                    <p className="font-sans text-[14px] text-[#1A3B5C]/70 leading-relaxed">{note.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {activeTab === 'stories' && <StoriesPlanner dynamicDays={storyDays.length > 0 ? storyDays : undefined} />}

      {!embedded && <ZeraPartners />}
      {!embedded && <Footer />}
    </div>

  );
};

export default InstagramContent;
